import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IRecord } from '@platform/core/types';
import { Spinner } from '@platform/ui';
import type { ILawDesignerTabProps } from './ILawDesignerTabProps';
import { sortedArticles } from './lawArticles';
import { analysisSections, workSections } from './lawCards';
import { LawAnalysis } from './LawAnalysis';
import { LawConflicts } from './LawConflicts';
import { LawFlow } from './LawFlow';
import { LawHeader } from './LawHeader';
import { LawImpacts } from './LawImpacts';
import { LawLinkMap } from './LawLinkMap';
import { LawOrgs } from './LawOrgs';
import { LawProblems } from './LawProblems';
import { LawRelations } from './LawRelations';
import { LawSections } from './LawSections';
import { LawStakes } from './LawStakes';
import { isLawTab, LAW_TABS } from './lawTabs';
import { LawWork } from './LawWork';
import { useLawLabels } from './useLawLabels';
import { useLawRecords } from './useLawRecords';
import './lawdesigner.css';

/**
 * Візуальна модель закону (прототип v4 vizLaw, вкладки lz_*): шапка з вибором законопроєкту й підсумком; далі — частини вкладки в порядку
 * прототипу: «Структура» (проблеми, структура закону, як працює закон, колізії, мапа актів), «Зацікавлені сторони» (матриця, впливи),
 * «Інтереси», «Відносини» (структури, суспільні відносини), «Аналітичні записи», «Правки». Без статей — підказка додати статті.
 */
export function LawDesignerTab({ ws, module, rt, record, tab }: ILawDesignerTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const moduleKey = module.module.key;
  const law = useLawRecords(ws, module, rt.key, record.id);
  const labels = useLawLabels(module);
  const arts = useMemo(() => sortedArticles(law.of('ldart')), [law]);
  const onOpen = useCallback((key: string, r: IRecord) => navigate(routes.record(moduleKey, key, r.id)), [navigate, moduleKey]);
  // Перемикач чернетки лишає ту саму вкладку конструктора явною в адресі (прототип: tab «lz_…», tabRec — пункт моделі закону підсвічено)
  const onDraft = (id: string) => navigate(routes.record(moduleKey, rt.key, id, isLawTab(tab) ? tab : LAW_TABS[0]));
  if (law.loading) return <div className="viz"><Spinner /></div>;
  const part = { law, arts, labels, onOpen };
  return (
    <div className="viz">
      <div className="law">
        <LawHeader {...part} ws={ws} rtKey={rt.key} draft={record} onDraft={onDraft} />
        {tab === 'law-records' && <LawAnalysis sections={analysisSections(law.of, arts, labels)} onOpen={onOpen} />}
        {tab === 'law-edits' && <LawWork sections={workSections(law.of, arts, labels)} onOpen={onOpen} />}
        {!arts.length && <div className="law__empty">{t('recordPage.law.empty')}</div>}
        {tab === 'law-structure' && <LawProblems {...part} />}
        {tab === 'law-relations' && <LawOrgs {...part} />}
        {(tab === 'law-stakeholders' || tab === 'law-interests') && <LawStakes {...part} list={tab === 'law-interests'} />}
        {tab === 'law-structure' && <LawSections {...part} />}
        {tab === 'law-structure' && <LawFlow {...part} />}
        {tab === 'law-relations' && <LawRelations {...part} />}
        <div className="law__pair">
          {tab === 'law-stakeholders' && <LawImpacts {...part} />}
          {tab === 'law-structure' && <LawConflicts {...part} />}
        </div>
        {tab === 'law-structure' && <LawLinkMap {...part} draft={record} />}
      </div>
    </div>
  );
}
